import * as React from 'react';
import { AlertTriangle, Check, Copy, Info, Loader2, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CopyId } from '@/components/page';
import { cn } from '@/lib/utils';
import type { ClientType, GrantType, OAuthClient } from '@/types';

export const API_URL: string = import.meta.env.VITE_API_URL || 'https://api.authrz.me';

/* ── option lists ───────────────────────────────────────── */

export const ALL_SCOPES = [
  'openid',
  'profile',
  'email',
  'offline_access',
  'products',
  'organizations',
] as const;

export const SCOPE_INFO: Record<string, string> = {
  openid: 'Required for OIDC. Returns an ID token.',
  profile: 'Name and profile picture.',
  email: 'Email address and verification status.',
  offline_access: 'Refresh token for long-lived access.',
  products: 'Per-app roles and permissions.',
  organizations: 'Organization membership.',
};

export const GRANTS: { value: GrantType; label: string; hint: string }[] = [
  {
    value: 'authorization_code',
    label: 'Authorization code',
    hint: 'Users sign in through the browser.',
  },
  {
    value: 'refresh_token',
    label: 'Refresh token',
    hint: 'Renew access without another sign-in.',
  },
  {
    value: 'client_credentials',
    label: 'Client credentials',
    hint: 'Machine-to-machine. Confidential clients only.',
  },
];

export const AUTH_METHODS: Record<string, string> = {
  none: 'none — PKCE only',
  client_secret_basic: 'client_secret_basic — HTTP Basic header',
  client_secret_post: 'client_secret_post — form body',
};

export const GRANT_SHORT: Record<string, string> = {
  authorization_code: 'code',
  refresh_token: 'refresh',
  client_credentials: 'client_credentials',
};

export const ENDPOINTS: [string, string][] = [
  ['Discovery', '/.well-known/openid-configuration'],
  ['Issuer', ''],
  ['Authorize', '/oauth/authorize'],
  ['Token', '/oauth/token'],
  ['UserInfo', '/oauth/userinfo'],
  ['JWKS', '/.well-known/jwks.json'],
  ['End session', '/oauth/logout'],
];

/* ── validation (RFC 8252-style redirect rules) ─────────── */

const LOOPBACK = ['localhost', '127.0.0.1', '[::1]'];

export function validateRedirectUri(raw: string, clientType: ClientType): string {
  const v = (raw || '').trim();
  if (!v) return 'Enter a redirect URI.';
  if (v.includes('*')) return 'Wildcards aren’t allowed — register each URI exactly.';
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return 'Enter a full URI including the scheme, e.g. https://app.example.com/callback.';
  }
  if (u.hash) return 'Redirect URIs can’t contain a fragment (#…).';
  const scheme = u.protocol.slice(0, -1).toLowerCase();
  if (['javascript', 'data', 'file', 'vbscript'].includes(scheme))
    return `The ${scheme}: scheme can’t be used for redirects.`;
  if (scheme === 'https') return u.hostname ? '' : 'Add a host after https://.';
  if (scheme === 'http')
    return LOOPBACK.includes(u.hostname)
      ? ''
      : 'Use https:// — plain http is only allowed for localhost and 127.0.0.1.';
  if (clientType !== 'public') return 'Custom schemes are only allowed for public (native) clients.';
  if (!/^[a-z][a-z0-9+-]*(\.[a-z0-9+-]+)+$/.test(scheme))
    return 'Custom schemes must use reverse-domain notation, e.g. com.example.app://callback.';
  return '';
}

/** Optional URLs (logout): https, or http on loopback. */
export function validateOptionalHttps(raw: string): string {
  const v = (raw || '').trim();
  if (!v) return '';
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return 'Enter a full URL including https://.';
  }
  if (u.protocol === 'https:') return '';
  if (u.protocol === 'http:' && LOOPBACK.includes(u.hostname)) return '';
  return 'Use https:// — plain http is only allowed for localhost and 127.0.0.1.';
}

/* ── small display bits ─────────────────────────────────── */

export function Tag({
  children,
  solid,
  className,
}: {
  children: React.ReactNode;
  solid?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-[5px] border px-1.5 py-0.5 font-mono text-[11.5px]',
        solid ? 'border-transparent bg-foreground/[0.07]' : 'border-border bg-card',
        className
      )}
    >
      {children}
    </span>
  );
}

export function Mono({ children, className }: { children: React.ReactNode; className?: string }) {
  return <code className={cn('font-mono text-[12.5px]', className)}>{children}</code>;
}

/** Callout in the design's `.alert` style (neutral / ok / warn / bad). */
export function Callout({
  tone = 'neutral',
  icon,
  title,
  children,
  action,
  className,
}: {
  tone?: 'neutral' | 'ok' | 'warn' | 'bad';
  icon?: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  const tones = {
    neutral: 'border-border bg-card [&_svg]:text-muted-foreground',
    ok: 'border-success/30 bg-success/[0.08] [&_svg]:text-success',
    warn: 'border-warning/35 bg-warning/[0.1] [&_svg]:text-warning',
    bad: 'border-destructive/35 bg-destructive/[0.08] [&_svg]:text-destructive',
  } as const;
  const defaultIcon =
    tone === 'ok' ? (
      <ShieldCheck className="h-4 w-4" />
    ) : tone === 'neutral' ? (
      <Info className="h-4 w-4" />
    ) : (
      <AlertTriangle className="h-4 w-4" />
    );
  return (
    <div
      role={tone === 'bad' || tone === 'warn' ? 'alert' : 'note'}
      className={cn('flex items-start gap-3 rounded-lg border p-3.5 text-sm', tones[tone], className)}
    >
      <span className="mt-0.5 shrink-0">{icon ?? defaultIcon}</span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        {children && <div className="mt-0.5 text-muted-foreground">{children}</div>}
      </div>
      {action}
    </div>
  );
}

/** Bordered code block with a header and copy button (design `.code-block`). */
export function CodeBlock({
  label,
  code,
  shown,
  className,
  maxHeight = 420,
}: {
  label: string;
  code: string;
  /** Text to display when it differs from what gets copied. */
  shown?: string;
  className?: string;
  maxHeight?: number;
}) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className={cn('min-w-0 overflow-hidden rounded-lg border border-border', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border bg-foreground/[0.03] py-1 pl-3 pr-1.5">
        <span className="truncate font-mono text-[11px] uppercase tracking-[0.05em] text-muted-foreground">
          {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={copy}
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <pre
        className="overflow-auto bg-card p-3 font-mono text-[12.5px] leading-relaxed"
        style={{ maxHeight }}
      >
        {shown ?? code}
      </pre>
    </div>
  );
}

/** Dotted label above a field. */
export function DevLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground"
    >
      {children}
    </label>
  );
}

/** Label / value definition grid (design `.kv`). */
export function KV({ rows }: { rows: [React.ReactNode, React.ReactNode][] }) {
  return (
    <dl>
      {rows.map(([k, v], i) => (
        <div
          key={i}
          className="grid grid-cols-1 gap-x-4 gap-y-1 border-t border-border py-3 first:border-t-0 first:pt-0 sm:grid-cols-[150px_minmax(0,1fr)]"
        >
          <dt className="text-[13px] text-muted-foreground">{k}</dt>
          <dd className="min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function InlineError({ id, children }: { id?: string; children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <p id={id} className="text-[12.5px] text-destructive" role="alert">
      {children}
    </p>
  );
}

export function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-[12.5px] text-muted-foreground">{children}</p>;
}

/** Skeleton rows for tables / cards in a loading state. */
export function SkeletonRows({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('divide-y divide-border', className)} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex animate-pulse items-center gap-4 px-4 py-3.5">
          <div className="h-4 w-40 rounded bg-foreground/[0.07]" />
          <div className="hidden h-4 w-24 rounded bg-foreground/[0.05] sm:block" />
          <div className="ml-auto h-4 w-16 rounded bg-foreground/[0.05]" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-4 py-12 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-foreground/[0.03] text-muted-foreground [&_svg]:h-5 [&_svg]:w-5">
        {icon}
      </div>
      <h3 className="text-[15px] font-semibold">{title}</h3>
      {children && <p className="mt-1 max-w-md text-sm text-muted-foreground">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Two-letter mark for a client / app. */
export function Mark({ name, className }: { name: string; className?: string }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-foreground/[0.04] font-mono text-sm font-semibold',
        className
      )}
    >
      {letters || '?'}
    </span>
  );
}

/* ── dialogs ────────────────────────────────────────────── */

/**
 * Confirm dialog. `typeToConfirm` requires the user to type an exact string
 * before the action is enabled (destructive moments).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  pendingLabel,
  danger,
  typeToConfirm,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  body: React.ReactNode;
  confirmLabel: string;
  pendingLabel?: string;
  danger?: boolean;
  typeToConfirm?: string;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  const ready = !typeToConfirm || typed === typeToConfirm;
  return (
    <Dialog open={open} onOpenChange={(v) => !pending && onOpenChange(v)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-muted-foreground">{body}</div>
          </DialogDescription>
        </DialogHeader>
        {typeToConfirm && (
          <div className="space-y-1.5">
            <DevLabel htmlFor="confirm-type">
              Type <span className="text-foreground normal-case">{typeToConfirm}</span> to confirm
            </DevLabel>
            <Input
              id="confirm-type"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={danger ? 'destructive' : 'default'}
            disabled={!ready || pending}
            onClick={() => void onConfirm()}
          >
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {pending ? (pendingLabel ?? confirmLabel) : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One-time secret moment: a full dialog that cannot be dismissed (no overlay
 * click, no Escape, no close button) until the user confirms it was stored.
 */
export function SecretDialog({
  open,
  title = 'Copy the client secret now',
  description,
  onDone,
  children,
  doneLabel = 'Done',
  ackLabel = 'I’ve stored the secret somewhere safe',
}: {
  open: boolean;
  title?: string;
  description: React.ReactNode;
  onDone: () => void;
  children: React.ReactNode;
  doneLabel?: string;
  ackLabel?: string;
}) {
  const [ack, setAck] = React.useState(false);
  React.useEffect(() => {
    if (open) setAck(false);
  }, [open]);
  return (
    <Dialog open={open}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] max-w-xl [&>button:last-child]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-3 rounded-lg border border-warning/35 bg-warning/[0.08] p-3.5">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div className="text-sm">
                <p className="font-semibold">Shown once</p>
                <p className="text-muted-foreground">
                  Put it in your secret manager or deployment environment before closing this
                  dialog.
                </p>
              </div>
            </div>
            {children}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} />
            <span>{ackLabel}</span>
          </label>
        </div>
        <DialogFooter>
          <Button type="button" disabled={!ack} onClick={onDone}>
            {doneLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Labelled secret value with its own copy affordance (wraps, never truncates). */
export function SecretField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <DevLabel>{label}</DevLabel>
      <CopyId value={value} className="w-full justify-between py-1.5 text-[13px] [&>span]:whitespace-normal [&>span]:break-all" />
    </div>
  );
}

/* ── form pieces ────────────────────────────────────────── */

/** Redirect URI repeater with per-row validation. */
export function UriListEditor({
  uris,
  onChange,
  clientType,
  errors,
  idPrefix = 'uri',
  minRows = 1,
  addLabel = 'Add URI',
}: {
  uris: string[];
  onChange: (uris: string[]) => void;
  clientType: ClientType;
  errors: Record<number, string>;
  idPrefix?: string;
  minRows?: number;
  addLabel?: string;
}) {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);
  const [focusLast, setFocusLast] = React.useState(false);
  React.useEffect(() => {
    if (focusLast) {
      refs.current[uris.length - 1]?.focus();
      setFocusLast(false);
    }
  }, [focusLast, uris.length]);
  return (
    <div>
      <div className="flex flex-col gap-2">
        {uris.map((u, i) => (
          <div key={i} className="space-y-1">
            <DevLabel htmlFor={`${idPrefix}-${i}`}>URI {i + 1}</DevLabel>
            <div className="flex items-center gap-2">
              <Input
                ref={(el) => {
                  refs.current[i] = el;
                }}
                id={`${idPrefix}-${i}`}
                className="min-w-0 flex-1 font-mono"
                value={u}
                onChange={(e) => onChange(uris.map((x, j) => (j === i ? e.target.value : x)))}
                placeholder={
                  clientType === 'public' ? 'com.example.app://callback' : 'https://app.example.com/callback'
                }
                autoComplete="off"
                spellCheck={false}
                aria-invalid={errors[i] ? true : undefined}
                aria-describedby={errors[i] ? `${idPrefix}-err-${i}` : undefined}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove URI ${i + 1}`}
                disabled={uris.length <= minRows}
                onClick={() => onChange(uris.filter((_, j) => j !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <InlineError id={`${idPrefix}-err-${i}`}>{errors[i]}</InlineError>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-3"
        onClick={() => {
          onChange([...uris, '']);
          setFocusLast(true);
        }}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        {addLabel}
      </Button>
    </div>
  );
}

/** Validate a redirect list: per-row messages plus duplicate detection. */
export function validateUriList(
  uris: string[],
  clientType: ClientType,
  requireOne: boolean
): Record<number, string> {
  const errs: Record<number, string> = {};
  const filled = uris.map((u) => u.trim());
  filled.forEach((u, i) => {
    if (!u) {
      if (requireOne && i === 0 && filled.every((x) => !x)) errs[i] = 'Enter a redirect URI.';
      return;
    }
    const msg = validateRedirectUri(u, clientType);
    if (msg) errs[i] = msg;
    else if (filled.indexOf(u) !== i) errs[i] = 'This URI is already in the list.';
  });
  return errs;
}

export function ScopePicker({
  value,
  onChange,
  disabledScopes = [],
  disabledNote = 'Not allowed for this client.',
  name = 'scope',
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabledScopes?: string[];
  disabledNote?: string;
  name?: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {ALL_SCOPES.map((s) => {
        const off = disabledScopes.includes(s);
        const checked = value.includes(s);
        return (
          <label
            key={s}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:border-foreground/30',
              checked && !off && 'border-foreground/40 bg-foreground/[0.03]',
              off && 'cursor-not-allowed opacity-55 hover:border-border'
            )}
          >
            <Checkbox
              name={name}
              className="mt-0.5"
              checked={checked && !off}
              disabled={off}
              onCheckedChange={() => onChange(checked ? value.filter((x) => x !== s) : [...value, s])}
            />
            <span className="min-w-0 flex-1">
              <code className="font-mono text-[12.5px] font-semibold">{s}</code>
              <span className="block text-[12.5px] text-muted-foreground">
                {off ? disabledNote : SCOPE_INFO[s]}
              </span>
            </span>
            {s === 'openid' && <Tag solid>OIDC</Tag>}
          </label>
        );
      })}
    </div>
  );
}

export function GrantPicker({
  value,
  onChange,
  clientType,
}: {
  value: GrantType[];
  onChange: (next: GrantType[]) => void;
  clientType: ClientType;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {GRANTS.map((g) => {
        const off = g.value === 'client_credentials' && clientType === 'public';
        const checked = value.includes(g.value) && !off;
        return (
          <label
            key={g.value}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:border-foreground/30',
              checked && 'border-foreground/40 bg-foreground/[0.03]',
              off && 'cursor-not-allowed opacity-55 hover:border-border'
            )}
          >
            <Checkbox
              className="mt-0.5"
              checked={checked}
              disabled={off}
              onCheckedChange={() =>
                onChange(checked ? value.filter((x) => x !== g.value) : [...value, g.value])
              }
            />
            <span className="min-w-0">
              <b className="block text-[13.5px] font-semibold">{g.label}</b>
              <small className="block text-[12.5px] text-muted-foreground">
                {off ? 'Needs a confidential client.' : g.hint}
              </small>
            </span>
          </label>
        );
      })}
    </div>
  );
}

/* ── snippets ───────────────────────────────────────────── */

export function authorizeSnippet(
  c: Pick<OAuthClient, 'client_id' | 'redirect_uris' | 'allowed_scopes' | 'pkce_required' | 'client_type'>
): string {
  const redirect = c.redirect_uris[0] || 'https://app.example.com/callback';
  const scope = c.allowed_scopes.join(' ');
  const pk = c.pkce_required || c.client_type === 'public';
  return `// 1 · Send the user to authrz.me
const url = new URL('${API_URL}/oauth/authorize');
url.search = new URLSearchParams({
  client_id: '${c.client_id}',
  redirect_uri: '${redirect}',
  response_type: 'code',
  scope: '${scope}',
  state: crypto.randomUUID(),${
    pk
      ? `
  code_challenge: await s256(codeVerifier),
  code_challenge_method: 'S256',`
      : ''
  }
});
location.assign(url);`;
}

export function tokenSnippet(
  c: Pick<
    OAuthClient,
    | 'client_id'
    | 'redirect_uris'
    | 'allowed_scopes'
    | 'allowed_grant_types'
    | 'client_type'
    | 'pkce_required'
    | 'token_endpoint_auth_method'
  >
): string {
  const redirect = c.redirect_uris[0] || 'https://app.example.com/callback';
  const codeFlow = c.allowed_grant_types.includes('authorization_code');
  if (!codeFlow && c.allowed_grant_types.includes('client_credentials')) {
    return `# Machine-to-machine token
curl -X POST ${API_URL}/oauth/token \\
  ${
    c.token_endpoint_auth_method === 'client_secret_post'
      ? `-d client_id=${c.client_id} \\
  -d client_secret="$CLIENT_SECRET" \\`
      : `-u "${c.client_id}:$CLIENT_SECRET" \\`
  }
  -d grant_type=client_credentials \\
  -d scope="${c.allowed_scopes.join(' ')}"`;
  }
  if (c.client_type === 'public') {
    return `# 2 · Exchange the code (no secret — the verifier proves it's you)
curl -X POST ${API_URL}/oauth/token \\
  -d grant_type=authorization_code \\
  -d code="$CODE" \\
  -d redirect_uri="${redirect}" \\
  -d client_id=${c.client_id} \\
  -d code_verifier="$CODE_VERIFIER"`;
  }
  const auth =
    c.token_endpoint_auth_method === 'client_secret_post'
      ? `-d client_id=${c.client_id} \\
  -d client_secret="$CLIENT_SECRET" \\`
      : `-u "${c.client_id}:$CLIENT_SECRET" \\`;
  return `# 2 · Exchange the code on your server
curl -X POST ${API_URL}/oauth/token \\
  ${auth}
  -d grant_type=authorization_code \\
  -d code="$CODE" \\
  -d redirect_uri="${redirect}"${
    c.pkce_required
      ? ` \\
  -d code_verifier="$CODE_VERIFIER"`
      : ''
  }`;
}

export const PKCE_SNIPPET = `// Generate a verifier and its S256 challenge
const bytes = crypto.getRandomValues(new Uint8Array(32));
const codeVerifier = base64url(bytes);
const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
const codeChallenge = base64url(new Uint8Array(digest));

function base64url(buf) {
  return btoa(String.fromCharCode(...buf)).replace(/\\+/g, '-').replace(/\\//g, '_').replace(/=+$/, '');
}`;

/* ── time helpers ───────────────────────────────────────── */

export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ago(v: string | null | undefined): string {
  if (!v) return 'Never';
  const ms = Date.now() - new Date(v).getTime();
  if (Number.isNaN(ms)) return '—';
  const s = Math.round(ms / 1000);
  if (s < 0) return 'just now';
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo} mo ago`;
  return `${Math.round(mo / 12)} y ago`;
}
